// PieChartLoadingState.jsx
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

const PulseAnimation = ({ attributeName, values, strokeWidth = false, animations }) => (
  <>
    <animate
      attributeName={attributeName}
      values={values}
      dur='1.8s'
      repeatCount='indefinite'
      keyTimes='0;0.5;1'
      calcMode='spline'
      keySplines='0.42 0 0.58 1; 0.42 0 0.58 1'
    />
    {strokeWidth && animations.isDonut && (
      <animate
        attributeName='stroke-width'
        values={`${animations.pulseRange.min};${animations.pulseRange.max};${animations.pulseRange.min}`}
        dur='1.8s'
        repeatCount='indefinite'
        keyTimes='0;0.5;1'
        calcMode='spline'
        keySplines='0.42 0 0.58 1; 0.42 0 0.58 1'
      />
    )}
  </>
)
PulseAnimation.propTypes = {
  attributeName: PropTypes.string.isRequired,
  values: PropTypes.string.isRequired,
  strokeWidth: PropTypes.bool,
  animations: PropTypes.shape({
    pulseRange: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number
    }),
    isDonut: PropTypes.bool
  })
}

const PieChartLoadingState = ({
  animate = true,
  height = 300,
  radius = [],
  grid = {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  }
}) => {
  const [innerRadius, outerRadius] = useMemo(() => {
    const parseRadiusValue = (value, fallback = 0) => {
      if (typeof value === 'number') return value
      if (typeof value === 'string' && value.trim().endsWith('%')) {
        return parseFloat(value.trim())
      }
      return parseFloat(value) || fallback
    }

    if (typeof radius === 'string') {
      return [0, parseRadiusValue(radius, 100)]
    }

    if (Array.isArray(radius)) {
      const inner = parseRadiusValue(radius[0], 0)
      const outer = parseRadiusValue(radius[1] || radius[0], 100)
      return [inner, outer]
    }

    return [0, 100]
  }, [radius])

  const dimensions = useMemo(() => {
    const innerRadiusPx = (innerRadius / 100) * (height / 2)
    const outerRadiusPx = (outerRadius / 100) * (height / 2)
    const strokeWidth = Math.min(outerRadiusPx - innerRadiusPx, 0.4 * height)
    const midRadius = innerRadiusPx + strokeWidth / 2
    const circumference = 2 * Math.PI * midRadius

    return {
      innerRadiusPx,
      outerRadiusPx,
      strokeWidth,
      midRadius,
      circumference
    }
  }, [height, innerRadius, outerRadius])

  const animations = useMemo(() => {
    const isDonut = dimensions.innerRadiusPx > 0
    const strokeLinecap = isDonut ? 'round' : 'butt'

    const pulseRange = isDonut
      ? {
        min: dimensions.strokeWidth * 0.7,
        max: dimensions.strokeWidth * 1.3
      }
      : {
        min: dimensions.strokeWidth,
        max: dimensions.strokeWidth
      }

    return {
      isDonut,
      strokeLinecap,
      pulseRange,
      dashArray: `${Math.PI * dimensions.midRadius} ${Math.PI * dimensions.midRadius}`,
      dashOffset: -Math.PI * dimensions.midRadius * 2
    }
  }, [dimensions])

  const baseCircleProps = {
    cx: height / 2,
    cy: height / 2,
    r: dimensions.midRadius,
    strokeWidth: dimensions.strokeWidth
  }

  return (
    <svg
      width={height}
      height={height}
      viewBox={`0 0 ${height} ${height}`}
      xmlns='http://www.w3.org/2000/svg'
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        margin: `${-grid.top - grid.bottom}px ${grid.left / 2}px 0 ${grid.left / 2}px`
      }}
    >
      <circle
        {...baseCircleProps}
        fill={animations.isDonut ? 'none' : 'var(--color-neutral-300)'}
        stroke={animations.isDonut ? 'var(--color-neutral-300)' : 'none'}
        strokeLinecap={animations.strokeLinecap}
      >
        {animate && (
          <PulseAnimation
            attributeName={animations.isDonut ? 'stroke-opacity' : 'fill-opacity'}
            values='0.3;1;0.3'
            strokeWidth
            animations={animations}
          />
        )}
      </circle>

      <circle
        {...baseCircleProps}
        fill='none'
        stroke={animate ? 'var(--color-neutral-400)' : 'var(--color-neutral-300)'}
        strokeLinecap={animations.strokeLinecap}
        strokeDasharray={animate ? animations.dashArray : undefined}
      >
        {animate && (
          <>
            <animate
              attributeName='stroke-dashoffset'
              from='0'
              to={animations.dashOffset}
              dur='2s'
              repeatCount='indefinite'
              calcMode='linear'
            />
            <PulseAnimation
              attributeName='stroke-opacity'
              values='0.3;1;0.3'
              strokeWidth
              animations={animations}
            />
          </>
        )}
      </circle>
    </svg>
  )
}

PieChartLoadingState.propTypes = {
  animate: PropTypes.bool,
  height: PropTypes.number,
  radius: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
  ]),
  grid: PropTypes.shape({
    left: PropTypes.number,
    right: PropTypes.number,
    top: PropTypes.number,
    bottom: PropTypes.number
  })
}

export default PieChartLoadingState
